import * as React from "react"
import { Slot } from "@radix-ui/react-slot"

import { cn } from "@/lib/utils"
import { Label } from "@/components/ui/label"
import { FormFieldError } from "@/lib/form/FormFieldError"

type TanstackFieldContextValue = {
  id: string
  invalid: boolean
}

const TanstackFieldContext = React.createContext<TanstackFieldContextValue | null>(null)

export const useTanstackField = () => {
  return React.useContext(TanstackFieldContext)
}

export function TanstackFieldWrapper({
  field,
  label,
  required,
  className,
  externalError,
  children
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  field: any
  label?: string | React.ReactNode
  required?: boolean
  className?: string
  externalError?: string
  children: React.ReactNode
}) {
  const isTouched = field.state?.meta?.isTouched ?? false;
  const isSubmitted =
    (field.form?.state?.isSubmitted ?? false) ||
    ((field.form?.state?.submissionAttempts ?? 0) > 0);
  const hasFieldError =
    Boolean(field.state.meta.errors && field.state.meta.errors.length > 0) &&
    (isTouched || isSubmitted);
  const invalid = hasFieldError || Boolean(externalError);
  const id = field.name;

  return (
    <TanstackFieldContext.Provider value={{ id, invalid }}>
      <div className={cn("space-y-1", className)} data-field={id}>
        {label && (
          <Label
            htmlFor={id}
            className={cn(
              "text-xs font-medium text-slate-600 transition-colors",
              invalid && "text-destructive font-bold"
            )}
          >
            {label} {required && <span className="text-destructive font-bold">*</span>}
          </Label>
        )}
        {children}
        <FormFieldError field={field} showUntouched={isSubmitted} />
        {!hasFieldError && externalError && (
          <p className="text-[11px] font-bold text-destructive mt-1" role="alert">
            {externalError}
          </p>
        )}
      </div>
    </TanstackFieldContext.Provider>
  )
}

export const TanstackFormControl = React.forwardRef<
  React.ElementRef<typeof Slot>,
  React.ComponentPropsWithoutRef<typeof Slot>
>(({ ...props }, ref) => {
  const context = useTanstackField()

  return (
    <Slot
      ref={ref}
      id={context?.id}
      aria-invalid={context?.invalid ? true : undefined}
      {...props}
    />
  )
})
TanstackFormControl.displayName = "TanstackFormControl"
